import Core from "@/core/Core";
import Partner from "@/domains/Partners/Partner";
import ContractsExternalDAO from "@/domains/Contracts/ContractsExternalDAO";
import ContractExternal from "@/domains/Contracts/ContractExternal";
import PartnersService from "@/domains/Partners/PartnersService";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";
import type OrderRequisites from "@/domains/Orders/OrderRequisites";

/**
 * Сервис для работы с внешними договорами
 */

class ContractsExternalService {

  static async get(id: number): Promise<ContractExternal | null> {
    const response = await ContractsExternalDAO.get(id);

    return Core.utils.hydrate(response.data, ContractExternal.fromServer);
  }

  static async getList(
    params?: Parameters<typeof ContractsExternalDAO.getList>[0]
  ): Promise<ContractExternal[]> {
    const response = await this.getListPaginated(params);
    return response.data;
  }

  /**
   * Пагинированная версия для DataGridLoader. Принимает фильтры от грида
   * и возвращает данные в форме { data, pagination }.
   *
   * Бэкенд для contracts.getListExternal сейчас не поддерживает пагинацию
   * и сортировку, поэтому соответствующие параметры игнорируются.
   */
  static async getListPaginated(
    params?: Parameters<typeof ContractsExternalDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    }
  ): Promise<{ data: ContractExternal[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await ContractsExternalDAO.getList(filters, pagination, sort);

    return {
      data: Core.utils.hydrateArray(response.data, ContractExternal.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /** ------- CRUD ------- */

  /**
   * Возвращает id созданного договора либо null, если добавить не удалось.
   */
  static async add(contract: ContractExternal): Promise<number | null> {
    const response = await ContractsExternalDAO.add(contract);

    if (response.ok) {
      Core.showNotification.success('Внешний договор успешно добавлен');
      return response.data;
    } else {
      return null;
    }
  }

  static async update(contract: ContractExternal): Promise<boolean> {
    const response = await ContractsExternalDAO.update(contract);

    if (response.ok) {
      Core.showNotification.success('Внешний договор успешно обновлен');
    }

    return response.ok;
  }

  static async delete(id: number, changeReasonText: string): Promise<boolean> {
    const response = await ContractsExternalDAO.delete(id, changeReasonText);

    if (response.ok) {
      Core.showNotification.success('Внешний договор успешно удален');
    }

    return response.ok;
  }

  /** ------- открытие диалога редактирования и создания контракта ------- */

  /**
   * Новый договор с партнёром-поставщиком: покупатель — текущий партнёр и его первое юрлицо.
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static createContract(partner: Partner): Promise<boolean> {
    const contractFromPartner = ContractExternal.fromPartner(partner);

    contractFromPartner.buyer = Core.currentUser!.partner;

    if (contractFromPartner.buyer && Core.currentUser?.partner.companies[0]) {
      contractFromPartner.buyerCompany = Core.currentUser?.partner.companies[0];
    }

    return this.showContractCard(contractFromPartner);
  }

  /**
   * Создание договора из заказа: стороны и их юр. лица берутся из реквизитов заказа.
   * Возвращает созданный договор либо null, если карточку закрыли без сохранения.
   *
   * Карточка резолвится булевым и созданный договор через промис не отдаёт,
   * поэтому забираем его событием created — по образцу PartnersService.createPartner.
   */

  static createContractFromOrderRequisites(requisites: OrderRequisites): Promise<ContractExternal | null> {
    const contract = ContractExternal.fromOrderRequisites(requisites);

    return new Promise((resolve) => {
      Core.dialogs.ContractExternalCard.show(
        {
          id: contract.id,
          contract
        },
        {
          [ContractExternal.Events.Created]: (created: ContractExternal) => { resolve(created); },
          close:                             () => { resolve(null); },
        },
      );
    });
  }

  /**
   * Показывает карточку договора: по id (карточка загрузит договор сама) или готовый договор
   * (без повторного запроса; новый договор — только так).
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static showContractCard(source: ContractExternal | number): Promise<boolean> {
    if (typeof source === 'number') {
      return Core.dialogs.ContractExternalCard.showAsPromise({ id: source });
    }
    else {
      return Core.dialogs.ContractExternalCard.showAsPromise({ id: source.id, contract: source });
    }
  }

  /**
   * Открывает ленту истории/комментариев договора
   */

  static showFeed(contractId: number): void {
    Core.dialogs.FeedDialog.show({
      entityTypeName: 'contractExternal',
      entityId:       contractId,
      showAddComment: false,
    });
  }

  /**
   * Удаляет договор с подтверждением и указанием причины.
   * Возвращает true если удаление произошло.
   */

  static async deleteWithConfirm(contract: ContractExternal): Promise<boolean> {
    const { isApproved, reasonText } = await Core.dialogs.confirmWithReason(
      'Удалить этот договор?',
      `Договор №${contract.number}`,
    );

    if (isApproved === true) {
      return await this.delete(contract.id, reasonText);
    } else {
      return false;
    }
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Пункты контекстного меню договора. Один источник и для грида реестра
   * (VDataGridV2 :context-menu), и для карточки (VDialogV3 :context-menu).
   * Грид обновляет список, а карточка закрывается через resolve(), только если
   * callback пункта вернул true.
   * Геттер, а не статика: callback'и завязаны на сервис.
   */

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (contract: ContractExternal) => this.showContractCard(contract),
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Перейти к поставщику',
        'person',
        (contract: ContractExternal) => { return PartnersService.showPartnerCard(contract.seller.id); },
      ),
      new ContextMenuItem(
        'История',
        'history',
        (contract: ContractExternal) => { this.showFeed(contract.id); },
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (contract: ContractExternal) => { return this.deleteWithConfirm(contract); },
        (contract: ContractExternal) => { return contract.isDeleted ? 'Договор уже удалён' : null; },
      ),
    ];
  }
}

export default ContractsExternalService;